/* akk-bok.js: the flip-book at /pages/fyrirtaeki?view=akk-bok (sections/akk-bok.liquid).
 *
 * Three ways to show the same page elements, picked from the stage's shape and the
 * reader's motion setting, and rebuilt when either changes:
 *   flip   - wide stage: StPageFlip (assets/akk-page-flip.js) turns real pages, cover alone.
 *   spread - wide stage + prefers-reduced-motion (or the library missing): the same
 *            spreads, swapped without animation.
 *   strip  - tall stage (phones): one page at a time on a native scroll-snap strip,
 *            so the swipe is the browser's own. This is also the no-script layout.
 * #p<N> opens page N and is kept in step with the reader (history.replaceState).
 * Without page images the section renders the Issuu reader; #p<N> becomes pageNumber.
 *
 * assets/akk-bok-shop.js (hotspots, box panel, contents) drives the book through
 * root.akkBok and follows it through the `akk:bok:page` event; it never touches
 * StPageFlip itself. data-turning is set on the root while a page is moving.
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-akk-bok]');
  if (!root) return;

  const hashPage = () => {
    const m = /^#p(\d+)$/.exec(location.hash);
    return m ? parseInt(m[1], 10) : 0;
  };

  if (root.dataset.mode === 'issuu') {
    const frame = root.querySelector('[data-akk-bok-issuu]');
    const p = hashPage();
    if (frame && p > 1) frame.src = `${frame.dataset.akkBokIssuu}&pageNumber=${p}`;
    return;
  }

  const $ = (s) => root.querySelector(s);
  const stage = $('[data-akk-bok-stage]');
  const book = $('[data-akk-bok-book]');
  if (!stage || !book) return;
  const pages = [...book.querySelectorAll('[data-akk-bok-page]')];
  const N = pages.length;
  if (!N) return;

  const ui = {
    prev: $('[data-akk-bok-prev]'),
    next: $('[data-akk-bok-next]'),
    count: $('[data-akk-bok-count]'),
    live: $('[data-akk-bok-live]'),
    scrub: $('[data-akk-bok-scrub]'),
    full: $('[data-akk-bok-full]'),
    hint: $('[data-akk-bok-hint]'),
  };
  const ratio = parseFloat(root.dataset.ratio) || 0.7071;
  const words = { page: root.dataset.wordPage || 'Síða', of: root.dataset.wordOf || 'af' };
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const coarse = matchMedia('(pointer: coarse)').matches;
  const clamp = (i) => Math.max(0, Math.min(N - 1, i));

  let mode = '';
  let pf = null;
  let cur = clamp(hashPage() - 1);
  let pageW = 0;

  /* The pages on screen for index i: the cover alone, then pairs (2-3, 4-5 …). */
  const visible = (i) => {
    if (mode === 'strip' || i === 0) return [i];
    const left = i % 2 ? i : i - 1;
    return left + 1 < N ? [left, left + 1] : [left];
  };

  const box = () => {
    const cs = getComputedStyle(stage);
    return {
      w: stage.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight),
      h: stage.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom),
    };
  };

  const pickMode = () => {
    const { w, h } = box();
    if (w / Math.max(h, 1) < 1.1) return 'strip';
    if (reduce.matches || !window.St || !window.St.PageFlip) return 'spread';
    return 'flip';
  };

  /* The pointer's last place, for the corner-zone check above. */
  let px = -1, py = -1;
  document.addEventListener('pointermove', (e) => { px = e.clientX; py = e.clientY; }, { capture: true, passive: true });
  const overMarker = () => {
    if (px < 0) return false;
    for (const s of book.querySelectorAll('.akk-bok__spot')) {
      const q = s.getBoundingClientRect();
      if (q.width && px >= q.left - 8 && px <= q.right + 8 && py >= q.top - 8 && py <= q.bottom + 8) return true;
    }
    return false;
  };

  const teardown = () => {
    if (pf) {
      try { pf.destroy(); } catch (e) { /* the root is gone either way */ }
      pf = null;
    }
    book.removeAttribute('style');
    pages.forEach((p) => {
      p.removeAttribute('style');
      p.className = 'akk-bok__page';
    });
    book.replaceChildren(...pages);
  };

  const buildFlip = () => {
    const { w, h } = box();
    let pw = h * ratio;
    let ph = h;
    if (pw * 2 > w) { pw = w / 2; ph = pw / ratio; }
    pageW = Math.floor(pw);
    const pageH = Math.floor(ph);
    const host = document.createElement('div');
    host.className = 'akk-bok__flip';
    book.style.width = `${pageW * 2}px`;
    book.style.height = `${pageH}px`;
    book.replaceChildren(host);
    pf = new window.St.PageFlip(host, {
      width: pageW,
      height: pageH,
      size: 'fixed',
      showCover: true,
      usePortrait: false,
      autoSize: true,
      drawShadow: true,
      maxShadowOpacity: 0.28,
      // Unhurried, closer to a hand turning paper (CEO: "a bit fast").
      flippingTime: 1100,
      mobileScrollSupport: false,
      swipeDistance: 24,
      showPageCorners: !coarse,
      clickEventForward: true,
      startPage: cur,
      startZIndex: 1,
    });
    pf.loadFromHTML(pages);
    // StPageFlip's hover peel follows the pointer anywhere within a fifth of the
    // page diagonal of a book corner, drawn as the leaf's back side on top of
    // the page, so a buy button placed in a top corner (akk-bok-shop.js) sat
    // under the flap and could not be pressed (Kristján, 2026-10-19). Over a
    // marker the corner zone yields: StPageFlip then takes its own READ path,
    // the peel folds back in about 60 ms and the button takes the press. The
    // check is by geometry because the flap, not the button, is under the
    // pointer while the peel is up.
    const ctl = typeof pf.getFlipController === 'function' ? pf.getFlipController() : pf.flipController;
    if (ctl && typeof ctl.isPointOnCorners === 'function') {
      const onCorners = ctl.isPointOnCorners.bind(ctl);
      ctl.isPointOnCorners = (pos) => onCorners(pos) && !overMarker();
    }
    pf.on('flip', (e) => { cur = clamp(e.data); update(); });
    pf.on('changeState', (e) => {
      if (e.data !== 'read') hideHint();
      // Markers go inert only while a page actually moves. StPageFlip also
      // reports 'fold_corner' whenever the pointer comes within a fifth of the
      // page diagonal of a corner (the hover peel), which covered every button
      // placed in a top corner: it lost its pointer events as the hand arrived,
      // the press fell through to the paper and turned the page instead
      // (Kristján, 2026-10-19).
      root.toggleAttribute('data-turning', e.data === 'flipping' || e.data === 'user_fold');
    });
  };

  const build = () => {
    const next = pickMode();
    if (next === mode && next !== 'flip') return;
    teardown();
    mode = next;
    root.dataset.view = mode;
    if (mode === 'flip') {
      try { buildFlip(); } catch (e) { teardown(); mode = 'spread'; root.dataset.view = mode; }
    }
    if (mode === 'strip') book.scrollLeft = cur * book.clientWidth;
    update(true);
  };

  // Load and decode nearby pages before they turn: a page decoded mid-flip
  // stalls the animation.
  const warm = (i) => {
    const img = i >= 0 && i < N && pages[i].querySelector('img');
    if (!img) return;
    if (img.loading !== 'eager') img.loading = 'eager';
    if (!img.dataset.akkDecoded && img.decode) {
      img.dataset.akkDecoded = '1';
      img.decode().catch(() => { delete img.dataset.akkDecoded; });
    }
  };

  const update = (initial) => {
    const v = visible(cur);
    const first = v[0];
    const last = v[v.length - 1];
    const label = v.map((x) => x + 1).join('–');
    if (ui.count) ui.count.textContent = label;
    if (ui.live && !initial) {
      const msg = `${words.page} ${label} ${words.of} ${N}`;
      ui.live.textContent = '';
      requestAnimationFrame(() => { ui.live.textContent = msg; });
    }
    if (ui.scrub) ui.scrub.value = String(first + 1);
    if (ui.prev) ui.prev.disabled = first <= 0;
    if (ui.next) ui.next.disabled = last >= N - 1;
    if (mode === 'spread') pages.forEach((p, i) => p.classList.toggle('is-shown', v.includes(i)));
    if (mode === 'flip') {
      let shift = 0;
      if (v.length === 1) shift = first === 0 ? -pageW / 2 : pageW / 2;
      book.style.transform = shift ? `translateX(${shift}px)` : '';
    }
    for (let d = -2; d <= 4; d++) { warm(first + d); warm(last + d); }
    const url = first ? `#p${first + 1}` : location.pathname + location.search;
    history.replaceState(history.state, '', url);
    root.dispatchEvent(new CustomEvent('akk:bok:page', { detail: { pages: v.map((x) => x + 1), mode } }));
  };

  const go = (i) => {
    i = clamp(i);
    if (mode === 'flip') {
      if (visible(i)[0] !== visible(cur)[0]) pf.flip(i);
    } else if (mode === 'strip') {
      book.scrollTo({ left: i * book.clientWidth, behavior: reduce.matches ? 'auto' : 'smooth' });
    } else if (i !== cur) {
      cur = i;
      update();
    }
  };
  const next = () => {
    hideHint();
    if (mode === 'flip') pf.flipNext();
    else go(visible(cur).slice(-1)[0] + 1);
  };
  const prev = () => {
    hideHint();
    if (mode === 'flip') pf.flipPrev();
    else go(visible(cur)[0] - 1);
  };

  /* strip: the scroll position is the page */
  let ticking = false;
  book.addEventListener('scroll', () => {
    if (mode !== 'strip' || ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      const i = clamp(Math.round(book.scrollLeft / Math.max(book.clientWidth, 1)));
      if (i !== cur) { cur = i; hideHint(); update(); }
    });
  }, { passive: true });

  ui.prev?.addEventListener('click', prev);
  ui.next?.addEventListener('click', next);
  ui.scrub?.addEventListener('change', () => go(parseInt(ui.scrub.value, 10) - 1));
  ui.scrub?.addEventListener('input', () => {
    if (mode !== 'flip') go(parseInt(ui.scrub.value, 10) - 1);
  });

  document.addEventListener('keydown', (e) => {
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
    if (root.querySelector('dialog[open]')) return;
    if (e.target.closest?.('input, textarea, select, [contenteditable]')) return;
    const k = e.key;
    if (k === 'ArrowRight' || k === 'PageDown') { e.preventDefault(); next(); }
    else if (k === 'ArrowLeft' || k === 'PageUp') { e.preventDefault(); prev(); }
    else if (k === 'Home') { e.preventDefault(); go(0); }
    else if (k === 'End') { e.preventDefault(); go(N - 1); }
  });

  addEventListener('hashchange', () => {
    const p = hashPage();
    if (p) go(p - 1);
  });

  /* fullscreen where the browser allows it (not iPhone Safari) */
  if (ui.full && document.fullscreenEnabled && root.requestFullscreen) {
    ui.full.hidden = false;
    ui.full.addEventListener('click', () => {
      if (document.fullscreenElement) document.exitFullscreen();
      else root.requestFullscreen().catch(() => {});
    });
    document.addEventListener('fullscreenchange', () => {
      ui.full.setAttribute('aria-pressed', String(document.fullscreenElement === root));
    });
  }

  /* "swipe to turn" on touch screens, until the first turn */
  let hintTimer = 0;
  function hideHint() {
    if (!ui.hint || ui.hint.hidden) return;
    ui.hint.hidden = true;
    clearTimeout(hintTimer);
  }

  let resizeTimer = 0;
  const onResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(build, 180);
  };
  addEventListener('resize', onResize);
  reduce.addEventListener?.('change', build);

  /* For akk-bok-shop.js: 1-based page numbers in and out. */
  root.akkBok = {
    go: (page) => { hideHint(); go(page - 1); },
    shown: () => visible(cur).map((x) => x + 1),
    count: N,
  };

  build();
  if (ui.hint && coarse) {
    ui.hint.hidden = false;
    hintTimer = setTimeout(hideHint, 5000);
  }
})();
